import React from 'react';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import { useScenario } from 'modules/project/providers/scenario';
import { i18n } from 'i18n';
import CreateScenarioDialog from './CreateScenarioDialog';

export interface ScenarioListItem {
  scenarioId: string;
  name: string;
  clonedFromScenarioId: string | null;
  createdAt: string;
  createdBy: string;
}

export interface ScenarioSidebarProps {
  scenarios: ScenarioListItem[];
  isLoading: boolean;
  onClone: (sourceScenarioId: string | null, name: string, cloneRecipes: boolean, clonePours: boolean) => Promise<void>;
  onDelete: (scenarioId: string) => Promise<void>;
}

const ScenarioSidebar = ({ scenarios, isLoading, onClone, onDelete }: ScenarioSidebarProps) => {
  const { activeScenarioId, setActiveScenario } = useScenario();
  const [dialogOpen, setDialogOpen] = React.useState<boolean>(false);
  const [dialogSourceScenarioId, setDialogSourceScenarioId] = React.useState<string | null>(null);
  const [dialogSourceLabel, setDialogSourceLabel] = React.useState<string>('Master');

  const findScenarioName = (id: string | null): string => {
    if (id === null) return 'master';
    const found = scenarios.find((s) => s.scenarioId === id);
    return found ? found.name : id;
  };

  const openCloneDialog = (sourceScenarioId: string | null, sourceLabel: string) => {
    setDialogSourceScenarioId(sourceScenarioId);
    setDialogSourceLabel(sourceLabel);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
  };

  const handleConfirmClone = async (sourceScenarioId: string | null, name: string, cloneRecipes: boolean, clonePours: boolean) => {
    await onClone(sourceScenarioId, name, cloneRecipes, clonePours);
  };

  const handleDelete = async (scenario: ScenarioListItem) => {
    // eslint-disable-next-line no-alert
    const confirmed = window.confirm(`${i18n._('Delete scenario')} "${scenario.name}"?`);
    if (!confirmed) return;
    await onDelete(scenario.scenarioId);
    if (activeScenarioId === scenario.scenarioId) {
      setActiveScenario(null, null);
    }
  };

  const isMasterActive = activeScenarioId === null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <AccountTreeIcon fontSize="small" color="secondary" />
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{i18n._('Scenario Manager')}</Typography>
      </Box>
      <Typography variant="caption" color="text.secondary">
        {i18n._('Branch and explore what-if changes without affecting master.')}
      </Typography>

      <Button
        size="small"
        color="secondary"
        variant="contained"
        startIcon={<ContentCopyIcon />}
        onClick={() => openCloneDialog(null, i18n._('Master'))}
        fullWidth
      >
        {i18n._('Clone Master to New Scenario')}
      </Button>

      <Paper
        variant="outlined"
        sx={{
          p: 1.25,
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
          bgcolor: isMasterActive ? 'action.selected' : 'background.paper',
          borderColor: isMasterActive ? 'secondary.main' : 'divider',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>{i18n._('Master')}</Typography>
          <Chip label={i18n._('MASTER')} color="secondary" size="small" sx={{ height: 18 }} />
        </Box>
        <Typography variant="caption" color="text.secondary">{i18n._('Read-only canonical state')}</Typography>
        <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
          <Button
            size="small"
            color="secondary"
            variant={isMasterActive ? 'contained' : 'outlined'}
            disabled={isMasterActive}
            startIcon={<SwapHorizIcon />}
            onClick={() => setActiveScenario(null, null)}
            sx={{ flex: 1 }}
          >
            {isMasterActive ? i18n._('Active') : i18n._('Activate')}
          </Button>
        </Box>
      </Paper>

      <Divider textAlign="left">
        <Typography variant="caption" color="text.secondary">{i18n._('User Scenarios')}</Typography>
      </Divider>

      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={28} />
        </Box>
      )}

      {!isLoading && scenarios.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
          {i18n._('No scenarios yet — clone master to get started.')}
        </Typography>
      )}

      {!isLoading && scenarios.map((scenario) => {
        const isActive = activeScenarioId === scenario.scenarioId;
        const clonedFromLabel = scenario.clonedFromScenarioId === null
          ? i18n._('Cloned from master')
          : `${i18n._('Cloned from')} ${findScenarioName(scenario.clonedFromScenarioId)}`;
        return (
          <Paper
            key={scenario.scenarioId}
            variant="outlined"
            sx={{
              p: 1.25,
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
              bgcolor: isActive ? 'action.selected' : 'background.paper',
              borderColor: isActive ? 'secondary.main' : 'divider',
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap title={scenario.name}>
              {scenario.name}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap title={clonedFromLabel}>
              {clonedFromLabel}
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
              <Button
                size="small"
                color="secondary"
                variant={isActive ? 'contained' : 'outlined'}
                disabled={isActive}
                startIcon={<SwapHorizIcon />}
                onClick={() => setActiveScenario(scenario.scenarioId, scenario.name)}
                sx={{ flex: 1 }}
              >
                {isActive ? i18n._('Active') : i18n._('Activate')}
              </Button>
              <Button
                size="small"
                color="secondary"
                variant="outlined"
                onClick={() => openCloneDialog(scenario.scenarioId, scenario.name)}
                sx={{ minWidth: 0, px: 1 }}
                aria-label="Clone"
              >
                <ContentCopyIcon fontSize="small" />
              </Button>
              <Button
                size="small"
                color="secondary"
                variant="outlined"
                onClick={() => handleDelete(scenario)}
                sx={{ minWidth: 0, px: 1 }}
                aria-label="Delete"
              >
                <DeleteIcon fontSize="small" />
              </Button>
            </Box>
          </Paper>
        );
      })}

      <CreateScenarioDialog
        open={dialogOpen}
        sourceScenarioId={dialogSourceScenarioId}
        sourceLabel={dialogSourceLabel}
        onClose={closeDialog}
        onConfirm={handleConfirmClone}
      />
    </Box>
  );
};

export default ScenarioSidebar;
